/**
 * Push Notification Utilities
 * Handles push notification subscription, permission requests, and notification display
 */

import HttpInterceptor from '../lib/HttpInterceptor';
import { PUSH_NOTIFICATION, PWA_CONFIG } from '../constants/appConstants';

let registration = null;
let subscription = null;

/**
 * Initialize push notifications (does not request permission automatically)
 * Only checks for existing subscriptions and VAPID configuration
 * Permission should be requested explicitly by user from settings page
 * @param {ServiceWorkerRegistration} swRegistration - Service worker registration
 * @returns {Promise<PushSubscription|null>}
 */
export async function initializePushNotifications(swRegistration) {
  if (!swRegistration) {
    // Silently fail - service worker may not be available
    return null;
  }

  registration = swRegistration;

  // Check if push notifications are supported
  if (!('PushManager' in window)) {
    // Not supported - silently return
    return null;
  }

  // Check if VAPID key is configured
  if (!PUSH_NOTIFICATION.VAPID_PUBLIC_KEY) {
    // VAPID key not configured - silently return (expected in development)
    return null;
  }

  try {
    // Only check for existing subscription, don't request permission
    subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('Push notification subscription found');
      return subscription;
    }

    // Check if permission was previously granted but no subscription exists
    // If permission is granted, we could auto-subscribe, but for better UX,
    // we'll wait for explicit user action
    const permission = Notification.permission;
    if (permission === PUSH_NOTIFICATION.PERMISSION.GRANTED) {
      // Permission granted but no subscription - user may want to subscribe later
      // Don't auto-subscribe, let user do it explicitly from settings
      return null;
    }

    // Permission not granted or default - silently return
    // User can enable notifications later from settings
    return null;
  } catch (error) {
    // Silently handle errors - push notifications are optional
    return null;
  }
}

/**
 * Request push notification permission
 * @returns {Promise<string>}
 */
export async function requestPermission() {
  if (!('Notification' in window)) {
    console.log('Notifications are not supported in this browser');
    return PUSH_NOTIFICATION.PERMISSION.DENIED;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return PUSH_NOTIFICATION.PERMISSION.DENIED;
  }
}

/**
 * Subscribe to push notifications
 * @returns {Promise<PushSubscription|null>}
 */
export async function subscribeToPush() {
  if (!registration) {
    console.error('Service worker registration is required');
    return null;
  }

  if (!PUSH_NOTIFICATION.VAPID_PUBLIC_KEY) {
    console.error('VAPID public key is not configured');
    return null;
  }

  try {
    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(PUSH_NOTIFICATION.VAPID_PUBLIC_KEY);

    // Subscribe to push service
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey,
    });

    // Validate subscription was created successfully
    if (!subscription) {
      console.error('Failed to create push subscription');
      return null;
    }

    console.log('Push notification subscription created:', subscription);

    // Send subscription to server (only if subscription is valid)
    await sendSubscriptionToServer(subscription);

    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    subscription = null; // Reset subscription on error
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<boolean>}
 */
export async function unsubscribeFromPush() {
  if (!subscription) {
    console.log('No subscription to unsubscribe from');
    return false;
  }

  try {
    const unsubscribed = await subscription.unsubscribe();
    
    if (unsubscribed) {
      // Remove subscription from server
      await removeSubscriptionFromServer(subscription);
      subscription = null;
      console.log('Push notification subscription removed');
    }

    return unsubscribed;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
}

/**
 * Send subscription to server
 * @param {PushSubscription} subscription
 * @returns {Promise<void>}
 */
async function sendSubscriptionToServer(subscription) {
  // Validate subscription before accessing its properties
  if (!subscription) {
    console.error('Cannot send undefined subscription to server');
    return;
  }

  if (!subscription.endpoint) {
    console.error('Subscription missing endpoint');
    return;
  }

  try {
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64(subscription.getKey('auth')),
      },
    };

    await HttpInterceptor.post(PUSH_NOTIFICATION.SUBSCRIPTION_ENDPOINT, subscriptionData);
    console.log('Subscription sent to server');
  } catch (error) {
    console.error('Error sending subscription to server:', error);
    throw error;
  }
}

/**
 * Remove subscription from server
 * @param {PushSubscription} subscription
 * @returns {Promise<void>}
 */
async function removeSubscriptionFromServer(subscription) {
  // Validate subscription before accessing its properties
  if (!subscription) {
    console.error('Cannot remove undefined subscription from server');
    return;
  }

  if (!subscription.endpoint) {
    console.error('Subscription missing endpoint');
    return;
  }

  try {
    await HttpInterceptor.delete(PUSH_NOTIFICATION.SUBSCRIPTION_ENDPOINT, {
      data: {
        endpoint: subscription.endpoint,
      },
    });
    console.log('Subscription removed from server');
  } catch (error) {
    console.error('Error removing subscription from server:', error);
    // Don't throw - subscription is already removed locally
  }
}

/**
 * Show local notification
 * @param {string} title
 * @param {NotificationOptions} options
 * @returns {Notification|null}
 */
export function showNotification(title, options = {}) {
  if (!('Notification' in window)) {
    console.log('Notifications are not supported');
    return null;
  }

  if (Notification.permission !== PUSH_NOTIFICATION.PERMISSION.GRANTED) {
    console.log('Notification permission not granted');
    return null;
  }

  const notificationOptions = {
    icon: PWA_CONFIG.NOTIFICATION.DEFAULT_ICON,
    badge: PWA_CONFIG.NOTIFICATION.BADGE,
    tag: PWA_CONFIG.NOTIFICATION.DEFAULT_TAG,
    ...options,
  };

  try {
    const notification = new Notification(title, notificationOptions);
    
    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      if (options.url) {
        window.open(options.url, '_blank');
      }
      notification.close();
    };

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
}

/**
 * Get current subscription
 * @returns {PushSubscription|null}
 */
export function getSubscription() {
  return subscription;
}

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export function isPushSupported() {
  return 'PushManager' in window && 'Notification' in window;
}

/**
 * Check if push notifications are enabled
 * @returns {Promise<boolean>}
 */
export async function isPushEnabled() {
  if (!isPushSupported()) {
    return false;
  }

  if (Notification.permission !== PUSH_NOTIFICATION.PERMISSION.GRANTED) {
    return false;
  }

  if (!registration) {
    return false;
  }

  try {
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

/**
 * Convert VAPID key from base64 URL to Uint8Array
 * @param {string} base64String
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

